/**
 * CRM service client (rialty-crm-service, through the gateway).
 *
 * `book` is 'personal' or 'brokerage'. A caller who can open several brokerage books (a
 * brokerage account) passes `ownerUserId` to say which; everyone else leaves it out.
 * Errors are left to the caller: read `err.response.status` (403, 404, 409, 503).
 */
import { apiClient, API_ENDPOINTS } from '../config/api';
import { getFirebaseToken } from '../utils/getFirebaseToken';
import { timeZoneParam } from '../utils/timezone';

const authHeaders = async () => ({ Authorization: `Bearer ${await getFirebaseToken()}` });

const ownerParam = (ownerUserId) => (ownerUserId ? { ownerUserId } : {});

export const crmService = {
  getBooks: async () => {
    const response = await apiClient.get(API_ENDPOINTS.crmBooks, { headers: await authHeaders() });
    return response.data;
  },

  listPersons: async (book, {
    ownerUserId, q, relationship, leadType, source, stage, addedFrom, addedTo, tag, sort, desc, page = 0, size = 25,
  } = {}) => {
    const params = {
      page,
      size,
      ...ownerParam(ownerUserId),
      ...(q && q.trim() && { q: q.trim() }),
      ...(relationship && { relationship }),
      ...(leadType && { leadType }),
      ...(source && { source }),
      ...(stage && { stage }),
      ...(addedFrom && { addedFrom }),
      ...(addedTo && { addedTo }),
      ...(tag && tag.trim() && { tag: tag.trim() }),
      ...(sort && { sort }),
      ...(sort && typeof desc === 'boolean' && { desc }),
      ...timeZoneParam(),
    };
    const response = await apiClient.get(API_ENDPOINTS.crmPersons(book), {
      headers: await authHeaders(),
      params,
    });
    return response.data;
  },

  getPerson: async (book, id, ownerUserId) => {
    const response = await apiClient.get(API_ENDPOINTS.crmPerson(book, id), {
      headers: await authHeaders(),
      params: ownerParam(ownerUserId),
    });
    return response.data;
  },

  createPerson: async (book, body, ownerUserId) => {
    const response = await apiClient.post(API_ENDPOINTS.crmPersons(book), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  updatePerson: async (book, id, body, ownerUserId) => {
    const response = await apiClient.patch(API_ENDPOINTS.crmPerson(book, id), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  /**
   * Adds up to 200 people at once. Resolves with a result for every row:
   * { total, created, duplicates, invalid, failed, results: [{ index, status, id, existingPersonId, message }] }.
   * One bad row never stops the others.
   */
  importPersons: async (book, rows, ownerUserId) => {
    const response = await apiClient.post(`${API_ENDPOINTS.crmPersons(book)}/import`, { rows }, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
      timeout: 120000,
    });
    return response.data;
  },

  archivePerson: async (book, id, ownerUserId) => {
    await apiClient.delete(API_ENDPOINTS.crmPerson(book, id), {
      headers: await authHeaders(),
      params: ownerParam(ownerUserId),
    });
  },

  // ---- pipeline ----

  /** The board for one side ('BUY' or 'SELL'). view: 'ACTIVE' (default), 'LOST' or 'WON'. */
  getPipeline: async (book, side, { view = 'ACTIVE', ownerUserId } = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.crmPipeline(book), {
      headers: await authHeaders(),
      params: { side, view, ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  /** What needs attention on `date` (yyyy-MM-dd, the user's own calendar day). */
  getToday: async (book, date, ownerUserId) => {
    const response = await apiClient.get(API_ENDPOINTS.crmToday(book), {
      headers: await authHeaders(),
      params: { date, ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  /** Changes one side of a person's pipeline. Resolves with the updated person. */
  updateLead: async (book, id, side, body, ownerUserId) => {
    const response = await apiClient.patch(API_ENDPOINTS.crmLead(book, id, side), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  /** Links a lead to the transaction made for it and marks it Won. Safe to repeat with the same id. */
  linkLeadToTransaction: async (book, id, side, transactionId, ownerUserId) => {
    const response = await apiClient.post(`${API_ENDPOINTS.crmLead(book, id, side)}/convert`, { transactionId }, {
      headers: await authHeaders(),
      params: ownerParam(ownerUserId),
    });
    return response.data;
  },

  logActivity: async (book, id, body, ownerUserId) => {
    const response = await apiClient.post(API_ENDPOINTS.crmActivities(book, id), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  sendEmail: async (book, id, body, ownerUserId) => {
    const response = await apiClient.post(API_ENDPOINTS.crmEmails(book, id), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  getActivities: async (book, id, ownerUserId) => {
    const response = await apiClient.get(API_ENDPOINTS.crmActivities(book, id), {
      headers: await authHeaders(),
      params: ownerParam(ownerUserId),
    });
    return response.data;
  },

  // ---- tasks ----

  /** A lead's tasks: open ones first (soonest due), then finished and skipped ones. */
  getTasks: async (book, id, ownerUserId) => {
    const response = await apiClient.get(API_ENDPOINTS.crmTasks(book, id), {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  /** body: { side?, title, type?, dueDate: 'yyyy-MM-dd' } */
  createTask: async (book, id, body, ownerUserId) => {
    const response = await apiClient.post(API_ENDPOINTS.crmTasks(book, id), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  /** body: any of { title, type, dueDate, status: 'DONE' | 'SKIPPED' | 'OPEN' }. DONE also writes to the timeline. */
  updateTask: async (book, id, taskId, body, ownerUserId) => {
    const response = await apiClient.patch(API_ENDPOINTS.crmTask(book, id, taskId), body, {
      headers: await authHeaders(),
      params: { ...timeZoneParam(), ...ownerParam(ownerUserId) },
    });
    return response.data;
  },

  deleteTask: async (book, id, taskId, ownerUserId) => {
    await apiClient.delete(API_ENDPOINTS.crmTask(book, id, taskId), {
      headers: await authHeaders(),
      params: ownerParam(ownerUserId),
    });
  },
};
